import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../../hooks/toast';
import { LiaBuildingSolid, LiaMapMarkerAltSolid } from 'react-icons/lia';
import {
  AInput,
  AInputSearch,
  ContainerForm,
  Loading,
  OptionFooter,
} from '../../../components';
import { Col, Form, Row } from 'antd';
import {
  cellPhoneMask,
  convertToLowerCase,
  mask,
  phoneMask,
} from '../../../shared/utils/functions';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { schemaCompany } from '../schemas/yup.company.schema';
import { ICompany } from '../../../shared/interfaces';
import { patchCompany, postCompany } from '../../../http';
import { useCompany } from '../../../hooks/company';

interface ICompanyData {
  company: ICompany | null;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const CompanyData: React.FC<ICompanyData> = ({
  company,
  loading,
  setLoading,
}) => {
  const { updateCompany } = useCompany();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [id, setId] = useState<string>();
  const [corporate_name, setCorporate_name] = useState<string>();
  const [fantasy_name, setFantasy_name] = useState<string>();
  const [cpf_cnpj, setCpf_cnpj] = useState<string>();
  const [phone, setPhone] = useState<string>();
  const [cell_phone, setCell_phone] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [cep, setCep] = useState<string>();
  const [address, setAddress] = useState<string>();
  const [number, setNumber] = useState<string>();
  const [complement, setComplement] = useState<string>();
  const [neighborhood, setNeighborhood] = useState<string>();
  const [city, setCity] = useState<string>();
  const [state, setState] = useState<string>(company?.state ?? 's');

  const handleSearchCnpj = useCallback(async () => {
    const CNPJ = cpf_cnpj?.replace(/[^0-9]/g, '');
    await axios
      .get(`${process.env.REACT_APP_BRASIL_API}cnpj/v1/${CNPJ}`)
      .then((response) => {
        setCorporate_name(response.data.razao_social);
        setFantasy_name(response.data.nome_fantasia);
        setPhone(phoneMask(response.data.ddd_telefone_1));
        setCell_phone(phoneMask(response.data.ddd_telefone_2));
        setEmail(response.data.email);
        setCep(response.data.cep);
        setAddress(response.data.logradouro);
        setNumber(response.data.numero);
        setNeighborhood(response.data.bairro);
        setCity(convertToLowerCase(response.data.municipio));
        setState(response.data.uf);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `Erro ao pesquisar CNPJ`,
        });
      });
  }, [cpf_cnpj]);

  const searchCep = useCallback(async () => {
    await axios
      .get(`${process.env.REACT_APP_BRASIL_API}cep/v1/${cep}`)
      .then((res) => {
        setCep(res.data.cep);
        setAddress(res.data.street);
        setNeighborhood(res.data.neighborhood);
        setCity(res.data.city);
        setState(res.data.state);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'CEP não encontrado',
          description: 'Não foi possível encontrar o Cep informado.',
        });
      });
  }, [addToast, cep]);

  const handleCreateSubmit = useCallback(() => {
    setLoading(true);
    schemaCompany
      .validate({
        corporate_name,
        fantasy_name,
        cpf_cnpj,
        phone,
        cell_phone,
        email,
        cep,
        address,
        number,
        complement,
        neighborhood,
        city,
        state,
      })
      .then(async (formValidated: any) => {
        await postCompany(formValidated)
          .then((data) => {
            updateCompany(data);
            addToast({
              type: 'success',
              title: 'Cadastrado',
              description: 'Cadastrado com sucesso.',
            });
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.error ?? 'Erro ao cadastrar',
            });
          })
          .finally(() => setLoading(false));
      });
  }, [
    corporate_name,
    fantasy_name,
    cpf_cnpj,
    phone,
    cell_phone,
    email,
    cep,
    address,
    number,
    complement,
    neighborhood,
    city,
    state,
  ]);

  const handleEditSubmit = useCallback(() => {
    setLoading(true);
    schemaCompany
      .validate({
        corporate_name,
        fantasy_name,
        cpf_cnpj,
        phone,
        cell_phone,
        email,
        cep,
        address,
        number,
        complement,
        neighborhood,
        city,
        state,
      })
      .then(async (formValidated: any) => {
        await patchCompany(id, formValidated)
          .then((data) => {
            updateCompany(data);
            addToast({
              type: 'info',
              title: 'Cliente alterado',
              description: 'Dados de cliente alterado com sucesso.',
            });
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.error ?? 'Erro ao alterar.',
            });
          })
          .finally(() => setLoading(false));
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors,
        });
      });
  }, [
    corporate_name,
    fantasy_name,
    cpf_cnpj,
    phone,
    cell_phone,
    email,
    cep,
    address,
    number,
    complement,
    neighborhood,
    city,
    state,
  ]);

  const loadDataCompany = useCallback(() => {
    if (company) {
      setId(company.id);
      setCorporate_name(company.corporate_name);
      setFantasy_name(company.fantasy_name);
      setCpf_cnpj(company.cpf_cnpj);
      setPhone(company.phone);
      setCell_phone(company.cell_phone);
      setEmail(company.email);
      setCep(company.cep);
      setAddress(company.address);
      setNumber(company.number);
      setComplement(company.complement);
      setNeighborhood(company.neighborhood);
      setCity(company.city);
      setState(company.state);
    }
  }, [company]);

  useEffect(() => {
    loadDataCompany();
  }, [company]);

  if (loading) return <Loading load />;

  return (
    <ContainerForm>
      <Form layout="vertical" style={{ width: '90%' }}>
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ marginBottom: 4 }}>
            <LiaBuildingSolid /> Dados da Empresa
          </h3>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Cadastre as principais informações institucionais da clínica.
          </p>
        </div>
        <Row>
          <Col span={8}>
            <Form.Item label="CPF/CNPJ" required>
              <AInputSearch
                placeholder="Informe o CPF/CNPJ"
                value={cpf_cnpj}
                maxLength={18}
                onSearch={handleSearchCnpj}
                onChange={(e) => setCpf_cnpj(mask(e.target.value))}
                enterButton
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Razão Social" required>
              <AInput
                placeholder="Informe a razão social"
                value={corporate_name}
                onChange={(e) => setCorporate_name(e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Nome Fantasia">
              <AInput
                placeholder="Informe o nome fantasia"
                value={fantasy_name}
                onChange={(e) => setFantasy_name(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={8}>
            <Form.Item label="Telefone" required>
              <AInput
                placeholder="Informe o telefone"
                value={phone}
                onChange={(e) => setPhone(phoneMask(e.target.value))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Celular">
              <AInput
                placeholder="Informe o celular"
                value={cell_phone}
                onChange={(e) => setCell_phone(cellPhoneMask(e.target.value))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="E-mail">
              <AInput
                placeholder="Informe o email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Form layout="vertical" style={{ width: '90%' }}>
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ marginBottom: 4 }}>
            <LiaMapMarkerAltSolid /> Endereço
          </h3>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Atualize os dados de localização usados nos documentos e
            comunicações.
          </p>
        </div>
        <Row>
          <Col span={6}>
            <Form.Item label="CEP" required>
              <AInputSearch
                placeholder="CEP"
                value={cep}
                onSearch={searchCep}
                maxLength={8}
                onChange={(e) => setCep(e.target.value)}
                enterButton
              />
            </Form.Item>
          </Col>
          <Col span={13}>
            <Form.Item label="Logradouro" required>
              <AInput
                placeholder="Informe o logradouro"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item label="Número">
              <AInput
                placeholder="Informe o número"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={8}>
            <Form.Item label="Bairro" required>
              <AInput
                placeholder="Informe o bairro"
                value={neighborhood}
                onChange={(e) => setNeighborhood(e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Cidade" required>
              <AInput
                placeholder="Informe a cidade"
                value={city || ''}
                onChange={(e) => setCity(e.target.value)}
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="UF" required>
              <AInput
                placeholder="Informe o UF"
                value={state}
                onChange={(e) => setState(e.target.value)}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item label="Complemento">
              <AInput
                placeholder="Informe o complemento"
                value={complement}
                onChange={(e) => setComplement(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <OptionFooter
          onClickCancel={() => navigate(-1)}
          onClickOk={id ? handleEditSubmit : handleCreateSubmit}
        />
      </Form>
    </ContainerForm>
  );
};

export default CompanyData;
